import { type Selection, build } from '@noths/polaris-product-listings-urls';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectHash, selectPathname } from 'src/redux/navigation/selectors';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { SortOption } from 'src/redux/productsQuery/types/SortOption';
import { extractBasePath } from 'src/utils/extractBasePath';
import { buildListingsURL } from '../builders/buildListingsURL';
import { buildListingsQueryParams } from './buildListingsQueryParams';

export type DeriveListingsURLPartsOptions = {
  excludeFilters?: boolean;
  facets?: Facet[];
  includeHash?: boolean;
  page?: number;
  sortOption?: SortOption | null;
};

export type ListingsURLParts = {
  hash: string;
  path: string;
  queryParams: URLSearchParams;
};

/**
 * Maps active facet options to the `{ alias, values }` selections the URL builder
 * consumes. Taxonomy and filter facets are returned together — `build` partitions
 * them by whether each alias is registered as taxonomy.
 */
export const facetsToSelections = (facets: Facet[]): Selection[] =>
  facets
    .map((facet) => ({
      alias: facet.alias,
      values: facet.options.filter((option) => option.active).map((option) => option.value),
    }))
    .filter((selection) => selection.values.length > 0);

export const deriveListingsURLParts = (
  state: ReduxApplicationState,
  options: DeriveListingsURLPartsOptions = {},
): ListingsURLParts => {
  const facets = options.facets ?? state.productsQuery.facets;
  const taxonomyAliases = state.productsQuery.taxonomyAttributeAliases ?? [];
  const basePath = state.productsQuery.surface
    ? `/shop/${state.productsQuery.surface}`
    : extractBasePath(selectPathname(state) ?? '/');

  const selections = facetsToSelections(facets);
  const filters = options.excludeFilters
    ? selections.filter((selection) => taxonomyAliases.includes(selection.alias))
    : selections;

  const path = build({
    basePath,
    filters,
    taxonomyAliases,
  });

  return {
    hash: options.includeHash === false ? '' : selectHash(state) ?? '',
    path,
    queryParams: buildListingsQueryParams(
      { productsQuery: { ...state.productsQuery, facets } },
      {
        page: options.page ?? state.productsQuery.page,
        sortOption: options.sortOption,
      },
    ),
  };
};

export const buildListingsURLFromState = (
  state: ReduxApplicationState,
  options: DeriveListingsURLPartsOptions = {},
): string => {
  const { hash, path, queryParams } = deriveListingsURLParts(state, options);
  return buildListingsURL(path, queryParams, hash);
};
